import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Upload,
  Button,
  Space,
  Typography,
  notification,
  Spin,
  Tooltip,
} from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import type { UploadProps } from "antd/es/upload";
import { useTranslation } from "react-i18next";
import { ENV } from "@/config/env";

const { Dragger } = Upload;
const { Text } = Typography;

interface FileUploadFieldProps {
  /** Current value - file ID or array of file IDs */
  value?: string | string[];
  /** Callback when value changes */
  onChange?: (value: string | string[] | undefined) => void;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Allow multiple files */
  multiple?: boolean;
  /** Accept file types (e.g., ".pdf,.doc,.docx") */
  accept?: string;
  /** Max file size in MB */
  maxSize?: number;
}

interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: number;
  contentType: string;
}

/**
 * Get icon based on file content type
 */
const getFileIcon = (contentType: string) => {
  if (contentType.startsWith("image/")) return <FileImageOutlined />;
  if (contentType === "application/pdf") return <FilePdfOutlined />;
  if (contentType.includes("word") || contentType.includes("document"))
    return <FileWordOutlined />;
  if (contentType.includes("excel") || contentType.includes("spreadsheet"))
    return <FileExcelOutlined />;
  return <FileOutlined />;
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

/**
 * File upload field component for Aras File type fields.
 * Provides drag-and-drop upload with file preview and management.
 */
export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  value,
  onChange,
  disabled = false,
  multiple = false,
  accept,
  maxSize = 50, // Default 50MB max
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  // Track which file IDs we've already fetched metadata for
  const initializedIdsRef = useRef<Set<string>>(new Set());

  // Fetch file metadata from API
  const fetchFileMetadata = useCallback(
    async (fileId: string): Promise<UploadedFile | null> => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await fetch(`${ENV.API_URL}/files/${fileId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          console.warn(`Failed to fetch metadata for file ${fileId}`);
          return null;
        }

        const data = await response.json();
        return {
          id: data.id,
          fileName: data.fileName || "Unknown file",
          fileSize: data.fileSize || 0,
          contentType: data.contentType || "application/octet-stream",
        };
      } catch (error) {
        console.error(`Error fetching file metadata:`, error);
        return null;
      }
    },
    [],
  );

  // Initialize from value prop (for edit mode)
  // Fetch actual file metadata from API
  useEffect(() => {
    if (!value) {
      // Value cleared - reset state only if we had files
      if (uploadedFiles.length > 0) {
        setUploadedFiles([]);
        initializedIdsRef.current = new Set();
      }
      return;
    }

    const ids = Array.isArray(value) ? value : [value];
    const newIds = ids.filter((id) => !initializedIdsRef.current.has(id));

    if (newIds.length === 0) return;

    // Fetch metadata for new file IDs
    const fetchMetadata = async () => {
      setLoadingMetadata(true);
      try {
        const results = await Promise.all(
          newIds.map((id) => fetchFileMetadata(id)),
        );

        const validFiles = results.filter((f): f is UploadedFile => f !== null);

        // For files that failed to fetch, create placeholders
        const failedIds = newIds.filter(
          (id) => !validFiles.some((f) => f.id === id),
        );
        const placeholders: UploadedFile[] = failedIds.map((id) => ({
          id,
          fileName: t("dynamicForm.existingFile", {
            defaultValue: "Existing file",
          }),
          fileSize: 0,
          contentType: "application/octet-stream",
        }));

        // Merge with existing files (that aren't being replaced)
        const existingFiles = uploadedFiles.filter(
          (f) => !newIds.includes(f.id),
        );
        setUploadedFiles([...existingFiles, ...validFiles, ...placeholders]);

        // Mark these IDs as initialized
        newIds.forEach((id) => initializedIdsRef.current.add(id));
      } finally {
        setLoadingMetadata(false);
      }
    };

    fetchMetadata();
  }, [value, fetchFileMetadata, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle file upload
  const handleUpload = useCallback(
    async (file: File): Promise<boolean> => {
      // Validate file size
      if (file.size > maxSize * 1024 * 1024) {
        notification.error({
          message: t("dynamicForm.fileTooLarge", {
            maxSize: `${maxSize}MB`,
            defaultValue: `File size exceeds ${maxSize}MB limit`,
          }),
        });
        return false;
      }

      setUploading(true);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const accessToken = localStorage.getItem("access_token");
        const response = await fetch(`${ENV.API_URL}/files/upload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `Upload failed: ${response.statusText}`,
          );
        }

        const result: UploadedFile = await response.json();

        // Update uploaded files list
        const newFile: UploadedFile = {
          id: result.id,
          fileName: result.fileName,
          fileSize: result.fileSize,
          contentType: result.contentType,
        };

        if (multiple) {
          const newFiles = [...uploadedFiles, newFile];
          setUploadedFiles(newFiles);
          onChange?.(newFiles.map((f) => f.id));
        } else {
          setUploadedFiles([newFile]);
          onChange?.(newFile.id);
        }

        notification.success({
          message: t("dynamicForm.fileUploaded", {
            fileName: file.name,
            defaultValue: `${file.name} uploaded successfully`,
          }),
        });

        return true;
      } catch (error) {
        notification.error({
          message:
            error instanceof Error
              ? error.message
              : t("dynamicForm.uploadFailed", {
                  defaultValue: "Upload failed",
                }),
        });
        return false;
      } finally {
        setUploading(false);
      }
    },
    [maxSize, multiple, uploadedFiles, onChange, t],
  );

  // Handle file removal
  const handleRemove = useCallback(
    (fileId: string) => {
      const newFiles = uploadedFiles.filter((f) => f.id !== fileId);
      setUploadedFiles(newFiles);

      if (multiple) {
        onChange?.(newFiles.length > 0 ? newFiles.map((f) => f.id) : undefined);
      } else {
        onChange?.(undefined);
      }
    },
    [uploadedFiles, multiple, onChange],
  );

  // Handle file download
  const handleDownload = useCallback(
    async (fileId: string, fileName: string) => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await fetch(
          `${ENV.API_URL}/files/${fileId}/download`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `Download failed: ${response.statusText}`,
          );
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        notification.error({
          message:
            error instanceof Error
              ? error.message
              : t("dynamicForm.downloadFailed", {
                  defaultValue: "Download failed",
                }),
        });
      }
    },
    [t],
  );

  // Handle file preview
  const handlePreview = useCallback(
    async (fileId: string, _contentType?: string) => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await fetch(`${ENV.API_URL}/files/${fileId}/view`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `Preview failed: ${response.statusText}`,
          );
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Open in new tab for preview
        window.open(url, "_blank");

        // Revoke URL after a delay to allow the new tab to load
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (error) {
        notification.error({
          message:
            error instanceof Error
              ? error.message
              : t("dynamicForm.previewFailed", {
                  defaultValue: "Preview failed",
                }),
        });
      }
    },
    [t],
  );

  // Upload props
  const uploadProps: UploadProps = {
    name: "file",
    multiple,
    accept,
    disabled: disabled || uploading,
    showUploadList: false,
    beforeUpload: async (file) => {
      await handleUpload(file);
      return false; // Prevent default upload behavior
    },
  };

  // Determine if we should show replacement upload
  const hasExistingFile = uploadedFiles.length > 0 && !multiple;
  const showUploadArea = !hasExistingFile || multiple;

  return (
    <div className="file-upload-field">
      {/* Loading state while fetching metadata */}
      {loadingMetadata && (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            border: "1px dashed #d9d9d9",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          <Spin />
          <p style={{ marginTop: 8, marginBottom: 0, color: "#666" }}>
            {t("dynamicForm.loadingFile", {
              defaultValue: "Loading file information...",
            })}
          </p>
        </div>
      )}

      {/* Upload area - shown when no file exists or multiple allowed */}
      {!loadingMetadata && showUploadArea && (
        <Dragger
          {...uploadProps}
          style={{ marginBottom: uploadedFiles.length ? 16 : 0 }}
        >
          {uploading ? (
            <div style={{ padding: "20px 0" }}>
              <Spin />
              <p style={{ marginTop: 8 }}>
                {t("dynamicForm.uploading", { defaultValue: "Uploading..." })}
              </p>
            </div>
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t("dynamicForm.uploadHint", {
                  defaultValue: "Click or drag file to this area to upload",
                })}
              </p>
              <p className="ant-upload-hint">
                {t("dynamicForm.uploadMaxSize", {
                  maxSize: `${maxSize}MB`,
                  defaultValue: `Maximum file size: ${maxSize}MB`,
                })}
              </p>
            </>
          )}
        </Dragger>
      )}

      {/* Uploaded files list */}
      {!loadingMetadata && uploadedFiles.length > 0 && (
        <div className="uploaded-files-list">
          {uploadedFiles.map((file) => (
            <div
              key={file.id}
              className="uploaded-file-item"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                marginBottom: 8,
                backgroundColor: "#fafafa",
              }}
            >
              <Space>
                {getFileIcon(file.contentType)}
                <div>
                  <Text strong style={{ display: "block" }}>
                    {file.fileName}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatFileSize(file.fileSize)}
                  </Text>
                </div>
              </Space>

              <Space>
                <Tooltip
                  title={t("buttons.preview", { defaultValue: "Preview" })}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreview(file.id, file.contentType)}
                  />
                </Tooltip>
                <Tooltip
                  title={t("buttons.download", { defaultValue: "Download" })}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(file.id, file.fileName)}
                  />
                </Tooltip>
                {!disabled && (
                  <Tooltip
                    title={t("buttons.delete", { defaultValue: "Delete" })}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemove(file.id)}
                    />
                  </Tooltip>
                )}
              </Space>
            </div>
          ))}
        </div>
      )}

      {/* Replace file button - shown when single file exists */}
      {!loadingMetadata && hasExistingFile && !disabled && (
        <Upload {...uploadProps} className="replace-file-upload">
          <Button icon={<SwapOutlined />} style={{ width: "100%" }}>
            {t("dynamicForm.replaceFile", {
              defaultValue: "Replace file",
            })}
          </Button>
        </Upload>
      )}
    </div>
  );
};

export default FileUploadField;
